import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token. Please use the link from your email.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Your link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-md mx-auto">
        <p className="label-eyebrow mb-4">Set a new password</p>
        <h1 className="font-serif text-4xl text-[#2D322B] mb-10">Choose a new password</h1>

        {!token ? (
          <div data-testid="reset-no-token" className="space-y-4">
            <p className="text-base text-[#5C6359]">This reset link is missing or invalid.</p>
            <Link to="/forgot-password" className="btn-primary inline-block" data-testid="reset-request-new">
              Request a new link
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="space-y-4">
            <p className="text-base text-[#5C6359]">Password updated. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-6" data-testid="reset-form">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">New password</label>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="reset-toggle-password"
                  className="text-[10px] uppercase tracking-widest text-[#5C6359] hover:text-[#C05C42] inline-flex items-center gap-1 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password"
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none transition-colors text-[#2D322B]"
              />
              <p className="text-xs text-[#9A8F7E] mt-2">At least 8 characters.</p>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Confirm new password</label>
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none transition-colors text-[#2D322B]"
              />
            </div>
            <button type="submit" disabled={busy} data-testid="reset-submit" className="btn-primary w-full">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
